import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { toast } from 'sonner';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
    useCreateSkillFrameworkMutation,
    useGetOrgSkillFrameworkTypesQuery,
    useGetPublicSkillFrameworkTypesQuery,
} from '@/state/skills/skills-api';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { getMessageByStatus } from '@/lib/error-message';
import { useClientPermissions } from '@/hooks/use-client-permissions';

const skillFrameworkSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    skill_type: z.string().min(1, 'Skill type is required'),
});

type FormValues = z.infer<typeof skillFrameworkSchema>;

interface CreateSkillFrameworkFormProps {
    tenant: string;
    isOpen: boolean;
    onClose: () => void;
    parentId?: string | number | null;
    onCreated?: () => void;
    variant?: 'framework' | 'skill';
}

const CreateSkillFrameworkForm = ({
    tenant,
    isOpen,
    onClose,
    parentId,
    onCreated,
    variant = 'framework',
}: CreateSkillFrameworkFormProps) => {
    const { userRoles } = useClientPermissions();
    const isSuperAdmin = userRoles.includes('SUPER ADMIN');
    const isSkillVariant = variant === 'skill';
    const dialogTitle = isSkillVariant ? 'Add skill' : 'Create Skill Framework';
    const dialogDescription = isSkillVariant
        ? 'Add a new skill under this category'
        : 'Add a new skill framework to your organization';
    const namePlaceholder = isSkillVariant
        ? 'Enter skill name'
        : 'Enter framework name';
    
    const [description, setDescription] = useState<string>('');
    
    const form = useForm<FormValues>({
        resolver: zodResolver(skillFrameworkSchema),
        defaultValues: {
            name: '',
            skill_type: '',
        },
    });

    const [createSkillFramework, { isLoading: isCreating }] =
        useCreateSkillFrameworkMutation();
    const { data: orgSkillTypes = [], isLoading: isOrgTypesLoading } =
        useGetOrgSkillFrameworkTypesQuery(
            { tenant },
            { skip: !tenant || isSuperAdmin }
        );
    const { data: publicSkillTypes = [], isLoading: isPublicTypesLoading } =
        useGetPublicSkillFrameworkTypesQuery(
            { tenant },
            { skip: !tenant || !isSuperAdmin }
        );

    const skillTypes = isSuperAdmin ? publicSkillTypes : orgSkillTypes;
    const isTypesLoading = isSuperAdmin ? isPublicTypesLoading : isOrgTypesLoading;
    const skillTypeOptions = [
        ...skillTypes.map((type) => ({
            id: String(type.id),
            name: type.name,
        })),
        { id: 'Other', name: 'Other' },
    ];

    const onSubmit = async (data: FormValues) => {
        try {
            await createSkillFramework({
                tenant,
                name: data.name,
                skill_type:
                    data.skill_type === 'Other'
                        ? null
                        : parseInt(data.skill_type),
                description: description || '',
                parent: parentId ? Number(parentId) : undefined,
            }).unwrap();

            toast.success('Framework created', {
                description: 'Your skill framework has been created successfully',
            });

            form.reset();
            setDescription('');
            onClose();
            onCreated?.();
        } catch (err) {
            const error = err as ApiErrorResponse;
            toast.error('Error creating framework', {
                description: getMessageByStatus(error?.status || 500),
            });
        }
    };

    const handleClose = () => {
        if (!isCreating) {
            form.reset();
            setDescription('');
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader className="mb-4">
                    <DialogTitle>{dialogTitle}</DialogTitle>
                    <DialogDescription>
                        {dialogDescription}
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6"
                    >
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder={namePlaceholder}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="skill_type"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Skill Type</FormLabel>
                                    <FormControl>
                                        <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={
                                                        isTypesLoading
                                                            ? 'Loading skill types...'
                                                            : 'Select skill type'
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {skillTypeOptions.map((type) => (
                                                    <SelectItem
                                                        key={type.id}
                                                        value={type.id}
                                                    >
                                                        {type.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <div className="space-y-1">
                                    <Textarea
                                        placeholder="Add a description for this skill..."
                                        value={description}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 1000) {
                                                setDescription(e.target.value);
                                            }
                                        }}
                                        className="resize-none"
                                        rows={4}
                                    />
                                    <div className="text-xs text-muted-foreground text-right">
                                        {description.length}/1000
                                    </div>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>

                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                                disabled={isCreating}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isCreating || !form.formState.isDirty}
                            >
                                {isCreating ? 'Creating...' : 'Create'}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default CreateSkillFrameworkForm;
